import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import PropTypes from "prop-types";
import { useMsalEvents } from "api/user/MsalEventListener";
import { useUserContext } from "api/user/UserProvider";
import { SCOPES } from "constants/constants";
import BrandedFullPageLoader from "core/loader/BrandedFullPageLoader";
import { isSsoContext } from "core/loader/loaderTheme";

const SSO_INTERACTIONS = new Set([
  InteractionStatus.Login,
  InteractionStatus.HandleRedirect,
  InteractionStatus.AcquireToken,
  InteractionStatus.SsoSilent,
]);

const ACTIVE_INTERACTIONS = new Set([
  InteractionStatus.Startup,
  ...SSO_INTERACTIONS,
]);

const MsalTokenForwarder = ({ children }) => {
  const { idToken, error, clearIdToken, clearError } = useMsalEvents();
  const { sendTokenToBack, setUserScope, setShowLoginFallbackHint } =
    useUserContext();
  const { inProgress } = useMsal();
  const sentRef = useRef(false);
  const [authenticating, setAuthenticating] = useState(false);

  useEffect(() => {
    document.getElementById("initial-loader")?.remove();
  }, []);

  useEffect(() => {
    if (!idToken || sentRef.current) return;
    sentRef.current = true;
    setAuthenticating(true);
    setUserScope(SCOPES.PERSO);
    sendTokenToBack(idToken)
      .catch((err) => {
        console.error("MSAL sendTokenToBack failed", err);
        sentRef.current = false;
        setAuthenticating(false);
        setShowLoginFallbackHint(true);
      })
      .finally(() => clearIdToken());
  }, [
    idToken,
    sendTokenToBack,
    setUserScope,
    setShowLoginFallbackHint,
    clearIdToken,
  ]);

  useEffect(() => {
    if (!error) return;
    console.error("MSAL error event", error);
    setShowLoginFallbackHint(true);
    clearError();
  }, [error, setShowLoginFallbackHint, clearError]);

  const showOverlay =
    ACTIVE_INTERACTIONS.has(inProgress) || authenticating || !!idToken;
  const isSsoFlow =
    SSO_INTERACTIONS.has(inProgress) || authenticating || !!idToken;

  if (showOverlay) {
    const useSsoTheme = isSsoFlow || isSsoContext();
    return (
      <BrandedFullPageLoader
        text={isSsoFlow ? "Connexion en cours…" : "Chargement…"}
        theme={useSsoTheme ? "sso" : "app"}
        overlay
      />
    );
  }
  return children;
};

MsalTokenForwarder.propTypes = {
  children: PropTypes.node,
};

export default MsalTokenForwarder;
